import filter from "lodash/filter"
import find from "lodash/find"
import toUpper from "lodash/toUpper"
import defer from "lodash/defer"
import isEmpty from "lodash/isEmpty"
import get from "lodash/get"
import noop from "lodash/noop"
import debounce from "lodash/debounce"
import { Component } from "react"
import PropTypes from "prop-types"
import closest from "utils-lib/closest"
import { AuthoringUtils, ModelManager } from "@adobe/aem-spa-page-model-manager"
import getIsTestEnv from "utils-lib/isUnitTest"
import dayjs from "utils-lib/date"
import "dayjs/locale/fr"
import { isMyWMApp } from "utils-lib/isMyWMApp"
import { isWMPath } from "utils-lib/isWMPath"
import {
  PATH_MAP,
  lob as LOB_CONSTANT,
  LINE_OF_BUSINESS,
  API_STATUSES,
  LANG_OPTIONS_MAP,
} from "constants-lib/common"
import {
  BOOTSTRAP_GRID_LG_MIN,
  BOOTSTRAP_GRID_SM_MAX,
} from "constants-lib/dimensions"
import { DEFAULT_SEARCH_DELAY } from "constants-lib/algolia"
import { withAuthentication } from "hoc-lib/appAuthentication"
import { withLDConsumer } from "launchdarkly-react-client-sdk"
import { getWindow } from "utils-lib/getWindow"
import { isNextApp } from "utils-lib/isNextApp"
import SkipLinks from "./components/SkipLinks/SkipLinks"
import BottomHeader from "./components/BottomHeader/BottomHeader"
import GlobalNotifications from "./components/GlobalNotifications/GlobalNotificationsContainer"
import MobileHeader from "./components/MobileHeader/MobileHeaderContainer"
import TopHeader from "./components/TopHeader/TopHeaderContainer"
import MegaNav from "./components/MegaNav/MegaNav"
import "./Header.scss"
import RedesignedHeader from "./components/RedesignedHeader/RedesignedHeader"

const ssrWindow = getWindow()

const DESKTOP_HEADER_HEIGHT = 117
const MOBILE_HEADER_HEIGHT = 55
const SCROLL_DESKTOP_HEADER_HEIGHT = 120
const SCROLL_MOBILE_HEADER_HEIGHT = 55
let isHamburgerOpen = false

export class Header extends Component {
  constructor(props) {
    super(props)

    this.state = {
      isMenuOpen: false,
      isPopoverOpen: false,
      isMenuPopoverOpen: false,
      isDesktopMenuWidth: false,
      megaNavSelection: ``,
      megaNavSubSelection: `Scheduled Pickup`,
      isClientSideLoaded: false,
    }

    this.checkCloseMegaNav = debounce(this.checkCloseMegaNav, 50, {
      trailing: true,
    })
  }

  // UNSAFE_componentWillMount() {
  //   this.handleResize()
  // }

  componentDidMount() {
    const {
      customerAddress,
      getGlobalAlerts,
      getIncidentsByUserId,
      getNotificationsByAddress,
      getNotificationsByUserId,
      userId,
      incidents,
      notifications,
      notificationsLanguage,
      siteLanguage = {},
      userAddress,
      alertsRead,
      getCombinedProductsUrl,
      ecommerceCart,
      isHeaderRendered,
      setHeaderStatus,
      getNotificationsByCustomerIdGuest,
      customerId,
      languageRoute,
    } = this.props

    this.handleResize()

    dayjs.locale("en")

    if (languageRoute.indexOf("fr") !== -1) {
      dayjs.locale("fr")
    }

    if (get(customerAddress, `formattedAddress`, ``)) {
      getCombinedProductsUrl(siteLanguage.language)
    }

    // this shouldn't be called in the Header, adding a check to prevent it from being called for wm-next as there is an issue with the redux hydration
    !isNextApp && getGlobalAlerts()

    getGlobalAlerts()

    if (
      (userId && isEmpty(incidents)) ||
      notificationsLanguage !== siteLanguage.language
    ) {
      getIncidentsByUserId()
    }

    if (
      isEmpty(notifications) ||
      notificationsLanguage !== siteLanguage.language
    ) {
      if (userId) {
        getNotificationsByUserId()
      } else if (isMyWMApp && customerId) {
        getNotificationsByCustomerIdGuest({ customerId })
      } else if (
        get(customerAddress, `formattedAddress`, ``) &&
        (!get(userAddress, `formattedAddress`, ``) || !alertsRead)
      ) {
        getNotificationsByAddress({ addressData: customerAddress })
      }
    }

    /* Check window width. This will be used to drive react-floater logic in TopMenuItem.
      Currently, widths >lg (>=992 px) render the desktop header and <992px render the mobile header.
      Mobile header does not need to utilize react-floater.
    */

    const LDFFReleasePersistentLiveChat = this.props.ldClient?.variation(
      "LDFFReleasePersistentLiveChat",
      false,
    )

    if (ecommerceCart?.id || userId) {
      this.props.getCartDetails(LDFFReleasePersistentLiveChat)
    }
    ssrWindow.addEventListener(`resize`, this.handleResize)
    ssrWindow.addEventListener(`scroll`, this.checkScroll)

    // Adjust top spacing after alert closes
    defer(() => {
      this.onAlertsChange()
    })

    if (!isMyWMApp) {
      if (!AuthoringUtils.isInEditor() && !isHeaderRendered) {
        setHeaderStatus()
      }
    }

    this.setState({ isClientSideLoaded: true })
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      customerAddress,
      getNotificationsByAddress,
      isAddressSearch,
      userAddress,
      alertsRead,
      setCustomerLob,
      getCombinedProductsUrl,
      siteLanguage,
      getCombinedProductUrlStatus,
      combinedProductSupportData,
      customerLob,
      userProducts,
      location,
      selectedLob,
      customerId,
      getIncidentsByUserId,
      getNotificationsByUserId,
      getNotificationsByCustomerIdGuest,
      guestCustomerIdToken,
      linkedAccounts,
      makeMinorAlertVisible,
      notificationsFetchState,
    } = this.props

    if (!isMyWMApp && location?.pathname !== prevProps?.location?.pathname) {
      const headerEl = ssrWindow.document.querySelector(`.Header`)

      if (headerEl && get(headerEl, `style`, null)) {
        headerEl.style.top = `0`

        this.setMadlibComponentTop(headerEl.clientHeight)
        this.setStickySubNavComponentTop(headerEl.clientHeight)
      }
    }

    if (this.state.isMenuOpen !== prevState.isMenuOpen) {
      this.onAlertClose()
    }

    if (
      get(prevProps, `alerts.length`, 0) !== get(this.props, `alerts.length`, 1)
    ) {
      this.onAlertsChange()
    }

    if (
      notificationsFetchState !== prevProps.notificationsFetchState &&
      notificationsFetchState === API_STATUSES.FULFILLED
    ) {
      makeMinorAlertVisible()
    }

    if (
      !isMyWMApp &&
      get(this.props, `alert.url`, ``) !== get(prevProps, `alert.url`, ``)
    ) {
      defer(() => {
        this.forceUpdate()
      })
    }

    if (isMyWMApp) {
      if (
        customerId &&
        guestCustomerIdToken !== prevProps.guestCustomerIdToken
      ) {
        getNotificationsByCustomerIdGuest({ customerId })
      } else if (
        !customerId &&
        isAddressSearch &&
        get(customerAddress, `formattedAddress`, ``) &&
        get(customerAddress, `formattedAddress`, ``) !==
          get(prevProps.customerAddress, `formattedAddress`, ``) &&
        (!get(userAddress, `formattedAddress`, ``) || !alertsRead)
      ) {
        getNotificationsByAddress({
          addressData: customerAddress,
        })
      }

      if (linkedAccounts?.length !== prevProps.linkedAccounts?.length) {
        getNotificationsByUserId()
        getIncidentsByUserId()
      }
    }

    if (
      !isMyWMApp &&
      isAddressSearch &&
      get(customerAddress, `formattedAddress`, ``) !==
        get(prevProps.customerAddress, `formattedAddress`, ``) &&
      (!get(userAddress, `formattedAddress`, ``) || !alertsRead)
    ) {
      getNotificationsByAddress({ addressData: customerAddress })
    }

    if (
      get(customerAddress, `formattedAddress`, ``) !==
      get(prevProps.customerAddress, `formattedAddress`, ``)
    ) {
      getCombinedProductsUrl(siteLanguage.language)
    }

    if (getCombinedProductUrlStatus !== prevProps.getCombinedProductUrlStatus) {
      const customers = get(combinedProductSupportData, `customers`, [])
      const customerResults = get(
        combinedProductSupportData,
        `customerResults`,
        ``,
      )

      const hasNoProducts = isEmpty(userProducts)

      // Update Lob for prospective-existing customer
      if (
        (customerResults === 200 || customerResults === 227) &&
        hasNoProducts
      ) {
        const lineOfBusiness = toUpper(
          get(customers, `[0].line_of_business`, ``),
        )

        if (isMyWMApp) {
          if (
            (selectedLob === LOB_CONSTANT.HOME || !selectedLob) &&
            (lineOfBusiness === LINE_OF_BUSINESS.COMMERCIAL ||
              lineOfBusiness === LINE_OF_BUSINESS.ROLLOFF)
          ) {
            setCustomerLob(LOB_CONSTANT.BUSINESS)
          } else if (
            (selectedLob === LOB_CONSTANT.BUSINESS || !selectedLob) &&
            lineOfBusiness === LINE_OF_BUSINESS.RESIDENTIAL
          ) {
            setCustomerLob(LOB_CONSTANT.HOME)
            // For 227 (existing user with multiple accounts, default to business)
          } else if (!selectedLob) {
            setCustomerLob(LOB_CONSTANT.BUSINESS)
          }
        } else if (!customerLob) {
          if (
            lineOfBusiness === LINE_OF_BUSINESS.COMMERCIAL ||
            lineOfBusiness === LINE_OF_BUSINESS.ROLLOFF
          ) {
            setCustomerLob(LOB_CONSTANT.BUSINESS)
          } else if (lineOfBusiness === LINE_OF_BUSINESS.RESIDENTIAL) {
            setCustomerLob(LOB_CONSTANT.HOME)
          } else {
            setCustomerLob(LOB_CONSTANT.BUSINESS)
          }
        }
      }
    }
  }

  componentWillUnmount() {
    ssrWindow.removeEventListener(`resize`, this.handleResize)
    ssrWindow.removeEventListener(`scroll`, this.checkScroll)
    ssrWindow.onscroll = null
  }

  handleScroll = () => {
    // Make header disappear on scroll down, reappear on scroll up
    let prevScrollpos = ssrWindow.pageYOffset

    const headerEl = ssrWindow.document.querySelector(`.Header`)

    // Check if header exists to be safe
    if (headerEl) {
      ssrWindow.onscroll = () => {
        this.toggleMainPageScrollY(isHamburgerOpen)
        const totalAlertHeight = this.getTotalAlertHeight()

        const currentScrollPos = ssrWindow.pageYOffset

        const headerHeight = this.state.isDesktopMenuWidth
          ? SCROLL_DESKTOP_HEADER_HEIGHT + totalAlertHeight
          : SCROLL_MOBILE_HEADER_HEIGHT + totalAlertHeight

        if (
          prevScrollpos > currentScrollPos ||
          currentScrollPos <= headerHeight ||
          currentScrollPos === 0 ||
          prevScrollpos === 0
        ) {
          const headerEl = ssrWindow.document.querySelector(`.Header`)
          if (get(headerEl, `style`, null)) {
            headerEl.style.top = `0`
            this.setMadlibComponentTop(headerEl.clientHeight)
            this.setStickySubNavComponentTop(headerEl.clientHeight)
          }
        } else if (!this.state.isMenuOpen) {
          const headerEl = ssrWindow.document.querySelector(`.Header`)

          if (get(headerEl, `style`, null)) {
            headerEl.style.top = this.state.isDesktopMenuWidth
              ? `-${headerHeight}px`
              : `-${headerHeight}px`
            this.setMadlibComponentTop(`0`)
            this.setStickySubNavComponentTop(`0`)
          }
        }
        prevScrollpos = currentScrollPos

        if (this.state.isPopoverOpen) {
          this.togglePopover(false)
        }

        if (this.state.isMenuPopoverOpen) {
          this.toggleMenuPopover(false)
          this.props.setShowMobileSearch(false)
        }

        if (this.state.megaNavSelection) {
          this.closeMegaNav()
        }
      }
      this.onMenuClick(false)
    }
  }

  setParentState = (state) => {
    this.setState(state)
  }

  setMadlibComponentTop = (height) => {
    const madlibContinerEl =
      ssrWindow.document.querySelector(`.Madlib-container`)
    if (madlibContinerEl) {
      madlibContinerEl.style.top = `${height}px`
    }
  }

  setStickySubNavComponentTop = (height) => {
    const windowWidth = ssrWindow.innerWidth
    const isMobileWidth = windowWidth <= BOOTSTRAP_GRID_SM_MAX
    const madlibContinerEl =
      ssrWindow.document.querySelector(`.Madlib-container`)
    const stickySubNavEl = ssrWindow.document.querySelector(
      `.StickySubNav-container`,
    )
    if (stickySubNavEl && madlibContinerEl && !isMobileWidth) {
      stickySubNavEl.style.top = `${height + madlibContinerEl.clientHeight}px`
    } else if (stickySubNavEl) {
      stickySubNavEl.style.top = `${height}px`
    }
  }

  onAlertsChange = () => {
    defer(() => {
      this.forceUpdate()
    })
  }

  onAlertClose = () => {
    // Adjust top spacing after alert closes
    defer(() => {
      this.forceUpdate()
    })
  }

  getTotalAlertHeight = () => {
    let alertHeight = 0

    const majorAlertEl = ssrWindow.document.querySelectorAll(`.MajorAlert`)
    const minorAlertEl = ssrWindow.document.querySelectorAll(`.MinorAlert`)
    const globalAlertEl = ssrWindow.document.querySelectorAll(`.GlobalAlert`)

    // Iterating because there are hidden alert elements with no height for some reason
    Array.from(majorAlertEl)
      .concat(Array.from(minorAlertEl))
      .concat(Array.from(globalAlertEl))
      .forEach((item) => {
        if (item.offsetHeight) {
          alertHeight += item.offsetHeight
        }
      })

    return alertHeight
  }

  checkScroll = () => {
    if (!ssrWindow.onscroll) {
      this.handleScroll()
    }
  }

  toggleMainPageScrollY = (isHamburgerOpen) => {
    if (isHamburgerOpen) {
      ssrWindow.document.body.classList.add("hide-page-scroll")
    } else {
      ssrWindow.document.body.classList.remove("hide-page-scroll")
    }
  }

  handleResize = () => {
    const windowWidth = ssrWindow.innerWidth
    const isDesktopMenuWidth = windowWidth >= BOOTSTRAP_GRID_LG_MIN

    /* If the window gets sized up to the desktop menu size, use a floater menu. */
    if (!this.state.isDesktopMenuWidth && isDesktopMenuWidth) {
      this.setState(
        {
          isDesktopMenuWidth: true,
        },
        () => {
          // Handle alert height after switch
          // Not needed for canada-app
          this.forceUpdate()
          this.handleScroll()
        },
      )
    } else if (this.state.isDesktopMenuWidth && !isDesktopMenuWidth) {
      // This else if block is not needed in canada-app and addresses heading spacing issues seen only in wm-aem-spa
      // when resizing the ssrWindow.
      this.setState(
        {
          isDesktopMenuWidth: false,
        },
        () => {
          this.handleScroll()
        },
      )
    }
  }

  closeMegaNav = () => {
    this.setState({
      megaNavSelection: ``,
      megaNavSubSelection: ``,
    })
  }

  checkCloseMegaNav = (event) => {
    if (
      !event.target.classList.contains(`MegaNav`) &&
      !event.target.classList.contains(`MenuItem`) &&
      !closest(event.target, `.MegaNav`) &&
      !closest(event.target, `.MenuItem`)
    ) {
      ssrWindow.document.removeEventListener(
        `mousemove`,
        this.checkCloseMegaNav,
      )

      this.closeMegaNav()
    }
  }

  onMegaNavSelection = (megaNavSelection) => {
    ssrWindow.document.removeEventListener(`mousemove`, this.checkCloseMegaNav)
    ssrWindow.document.addEventListener(`mousemove`, this.checkCloseMegaNav)

    const selectedObj = find(
      this.props.navigationDetails,
      (item) => item.firstLevelNavigationText === megaNavSelection,
    )

    const megaNavSubSelection = get(
      selectedObj,
      `secondLevelSections[0].secondLevelHeading`,
      ``,
    )

    this.setState({
      megaNavSelection,
      megaNavSubSelection,
      detailsPaneData: selectedObj,
    })
  }

  onMenuClick = (isOpen) => {
    if (isMyWMApp) {
      isHamburgerOpen = isOpen
      this.setState({
        isMenuOpen: isOpen,
      })
    } else {
      isHamburgerOpen = isOpen
      this.toggleMainPageScrollY(isHamburgerOpen)
      if (isOpen !== this.state.isMenuOpen) {
        this.props.setMenuOpen(isOpen)
        defer(() => {
          this.setState(
            {
              isMenuOpen: isOpen,
            },
            () => {
              if (!isOpen) {
                this.closeMegaNav()
              }
            },
          )
        })
      } else if (!isOpen) {
        this.closeMegaNav()
      }
    }
  }

  togglePopover = (isOpen) => {
    this.setState({ isPopoverOpen: isOpen })
  }

  toggleMenuPopover = (isOpen) => {
    this.setState({ isMenuPopoverOpen: isOpen })
  }

  renderMegaNav(totalAlertHeight) {
    const { megaNavSelection, megaNavSubSelection, detailsPaneData } =
      this.state

    const { navigationDetails } = this.props

    return (
      <MegaNav
        navigationDetails={navigationDetails}
        detailsPaneData={detailsPaneData}
        megaNavSelection={megaNavSelection}
        megaNavSubSelection={megaNavSubSelection}
        onMenuClick={this.onMenuClick}
        setParentState={this.setParentState}
        totalAlertHeight={totalAlertHeight}
      />
    )
  }

  render() {
    const { isDesktopMenuWidth, megaNavSelection, isClientSideLoaded } =
      this.state

    const languages = [
      {
        label: `US - English`,
        code: `en_US`,
        url: `/content/dam/wm/icons/us-flag-icon-small.svg`,
        altText: `USA`,
      },
      {
        label: `CA - English`,
        code: `en_CA`,
        url: `/content/dam/wm/icons/ca-flag-icon-small.svg`,
        altText: `Canada`,
      },
      {
        label: `CA - French`,
        code: `fr_CA`,
        url: `/content/dam/wm/icons/ca-flag-icon-small.svg`,
        altText: `Canada`,
      },
      // Commenting out this till the Spanish page is going Live on Prod
      // {
      //   label: `US - Spanish`,
      //   code: `es_US`,
      //   url: `/content/dam/wm/icons/ca-flag-icon-small.svg`,
      //   altText: `USA`,
      // },
    ]

    const {
      alerts,
      navigationDetails,
      copyright,
      customerLob,
      ecommerceCart,
      failureTitle,
      failureDescription,
      getCartDetails,
      hasFrPage,
      indexList,
      isSingleAccount,
      isSupportSearch,
      languageRoute,
      legalLinks,
      location,
      logo,
      logoLink,
      logoAltText,
      navigation,
      numCharacters,
      numHighlightedResults,
      numResults,
      numberQueries,
      pageLocales,
      queryIndex,
      filterType,
      searchBarPlaceholderText,
      setShowMobileSearch,
      showMobileSearch,
      siteLanguage,
      resultsIndex,
      utilityBarLinks: authoredUtilityBarLinks, // renaming this prop to keep the changes minimal
      userProducts,
      userState,
      errorMessageTitle,
      errorMessageDescription,
      suggestedText,
      resultsText,
      seeAllResultsText,
      customerAddress,
      searchDelay,
      loginDropdownLabel,
      loginEmailPlaceholderText,
      loginPasswordPlaceholderText,
      loginButtonCta,
      forgotEmailPwText,
      continueAsGuestText,
      createProfileText,
      myWmDropdownLabel,
      myWmMenuHeading,
      logoutCtaText,
      ssrModel,
      localsitesCdn,
      isLoggedIn,
      firstName,
      emailId,
      mobilePhone,
      selectedAccountDetails,
      notifications,
      notificationsFetchState,
      facilityLocator,
      setIsCustomerSelectClicked,
      setNewMultiAccountTabSelected,
      multiAccountDashboard,
      isBrokerAccount,
    } = this.props

    let bodyWidth

    const isFrench = siteLanguage?.language === LANG_OPTIONS_MAP.FR_CA

    // This should be added in AEM authoring with no FE code change, but due to time constraints on an ask from leadership we are hardcoding it here.
    const hardCodedHotFixUtilityBarLinks = [
      {
        isExternalLink: false,
        utilityLink: `${languageRoute}/support`,
        analyticsKey: isFrench ? "Soutien" : "Support",
        utilityType: "support",
        cartIcon: null,
        cartIconAltText: null,
        mobileNotificationIcon: null,
        activeNotificationIconInMobile: null,
        activeNotificationIconInMobileAltText: null,
        mobileNotificationIconAltText: null,
        utilityLabel: isFrench ? "Soutien" : "Support",
      },
      ...authoredUtilityBarLinks,
    ]

    const utilityBarLinks = hardCodedHotFixUtilityBarLinks

    if (isMyWMApp) {
      bodyWidth = ssrWindow.document.body.offsetWidth
    }
    const pageLanguages = filter(
      languages,
      (language) =>
        !isEmpty(pageLocales) && pageLocales.indexOf(language.code) !== -1,
    )
    // iPad cannot hover so show mobile nav
    let showMegaNav

    if (isMyWMApp) {
      showMegaNav =
        bodyWidth >= BOOTSTRAP_GRID_LG_MIN &&
        ssrWindow.navigator.userAgent.match(/iPad/i) === null
    } else {
      showMegaNav =
        isDesktopMenuWidth &&
        ssrWindow.navigator.userAgent.match(/iPad/i) === null
    }

    const isHomePage =
      location?.pathname === `` ||
      location?.pathname === `/` ||
      location?.pathname?.endsWith(languageRoute) ||
      location?.pathname?.endsWith(`${languageRoute}/`)

    const spacingHeight = showMegaNav
      ? DESKTOP_HEADER_HEIGHT
      : MOBILE_HEADER_HEIGHT

    const totalAlertHeight = this.getTotalAlertHeight()

    let finalSpacingHeight
    let pageData = {}

    if (!isMyWMApp) {
      finalSpacingHeight = spacingHeight

      let pageModelPath

      if (isHomePage) {
        if (customerLob === ``) {
          pageModelPath = `${languageRoute}${PATH_MAP.NO_LOB_PATH}`
        } else if (customerLob === `home`) {
          pageModelPath = `${languageRoute}${PATH_MAP.HOME_PATH}`
        } else if (customerLob === `business`) {
          pageModelPath = `${languageRoute}${PATH_MAP.BUSINESS_PATH}`
        }
      } else {
        pageModelPath = location.pathname
      }

      const isTest = getIsTestEnv()

      // To support nextJs apps, we will use the ssrModel if passed in
      if (!isTest) {
        pageData = !isEmpty(ssrModel)
          ? ssrModel
          : ModelManager.modelStore.getData(pageModelPath)
      }

      /* hide the header if we are using the simpleNav component to prevent multiple nav components from being rendered */
      if (
        !isTest &&
        ((!AuthoringUtils.isInEditor() && isEmpty(pageData)) ||
          navigation === "simpleNavigation")
      ) {
        return null
      }
    }

    const incidentsByDate = get(notifications, `incidentsByDate`, [])
    const facilityAlertsList = get(facilityLocator, `alerts`, [])

    const hasNotification =
      ((notificationsFetchState === `fulfilled` ||
        notificationsFetchState === `failed`) &&
        !isEmpty(notifications)) ||
      !isEmpty(incidentsByDate) ||
      !isEmpty(facilityAlertsList)
    const multiAccount = !isSingleAccount && !isBrokerAccount
    const useRedesignedHeader =
      isLoggedIn && isWMPath() && (multiAccount || isSingleAccount)

    return useRedesignedHeader ? (
      <header>
        <RedesignedHeader
          navigationDetails={navigationDetails}
          getCartDetails={getCartDetails}
          logo={logo}
          logoLink={logoLink}
          logoAltText={logoAltText}
          indexList={indexList}
          suggestionIndex={resultsIndex}
          resultsIndex={resultsIndex}
          queryIndex={queryIndex}
          filterType={filterType}
          numResults={numResults}
          numHighlightedResults={numHighlightedResults}
          numberQueries={numberQueries}
          numCharacters={numCharacters}
          errorMessageTitle={errorMessageTitle}
          errorMessageDescription={errorMessageDescription}
          failureTitle={failureTitle}
          failureDescription={failureDescription}
          suggestedText={suggestedText}
          resultsText={resultsText}
          seeAllResultsText={seeAllResultsText}
          searchDelay={searchDelay}
          ecommerceCart={ecommerceCart}
          siteLanguage={siteLanguage}
          setParentState={this.setParentState}
          setShowMobileSearch={setShowMobileSearch}
          showMobileSearch={showMobileSearch}
          isSupportSearch={isSupportSearch}
          firstName={firstName}
          alerts={alerts}
          onAlertsChange={this.onAlertsChange}
          onAlertClose={this.onAlertClose}
          languages={
            !isEmpty(pageLocales) && pageLocales.length > 0
              ? pageLanguages
              : languages
          }
          isPopoverOpen={this.state.isMenuPopoverOpen}
          togglePopover={this.toggleMenuPopover}
          hasNotification={hasNotification}
          onMenuClick={this.onMenuClick}
          isMenuOpen={this.state.isMenuOpen}
          isSingleAccount={isSingleAccount}
          {...{
            setIsCustomerSelectClicked,
            setNewMultiAccountTabSelected,
            multiAccountDashboard,
            emailId,
            mobilePhone,
            selectedAccountDetails,
          }}
        />
      </header>
    ) : (
      <header data-testid="Header">
        {/* Adds padding at the top */}
        <div
          className="header-spacing"
          style={{
            background: `#fff`,
            height: isMyWMApp ? spacingHeight : finalSpacingHeight,
          }}
        />
        <div className="Header maax-font-weight-regular">
          <SkipLinks />
          {showMegaNav ? (
            <div
              className="topListItems position-relative desktop-header"
              style={{
                display:
                  isClientSideLoaded &&
                  showMegaNav &&
                  ssrWindow.innerWidth >= BOOTSTRAP_GRID_LG_MIN
                    ? "block"
                    : "none",
              }}
            >
              {!isMyWMApp && (
                <GlobalNotifications
                  alerts={alerts}
                  onAlertsChange={this.onAlertsChange}
                  onAlertClose={this.onAlertClose}
                  megaNavSelection={megaNavSelection}
                />
              )}
              <TopHeader
                isPopoverOpen={this.state.isPopoverOpen}
                isSingleAccount={isSingleAccount}
                togglePopover={this.togglePopover}
                utilityBarLinks={utilityBarLinks}
                loginDropdownLabel={loginDropdownLabel}
                loginEmailPlaceholderText={loginEmailPlaceholderText}
                loginPasswordPlaceholderText={loginPasswordPlaceholderText}
                loginButtonCta={loginButtonCta}
                forgotEmailPwText={forgotEmailPwText}
                continueAsGuestText={continueAsGuestText}
                createProfileText={createProfileText}
                myWmMenuHeading={myWmMenuHeading}
                logoutCtaText={logoutCtaText}
                myWmDropdownLabel={myWmDropdownLabel}
                getCartDetails={getCartDetails}
                ecommerceCart={ecommerceCart}
                localsitesCdn={localsitesCdn}
                languages={
                  !isEmpty(pageLocales) && pageLocales.length > 0
                    ? pageLanguages
                    : languages
                }
                isSSR={!isEmpty(ssrModel)}
                ssrModel={ssrModel}
              />
              <BottomHeader
                navigationDetails={navigationDetails}
                closeMegaNav={this.closeMegaNav}
                languageRoute={languageRoute}
                logo={logo}
                logoLink={logoLink}
                logoAltText={logoAltText}
                megaNavSelection={megaNavSelection}
                onMegaNavSelection={this.onMegaNavSelection}
                customerLob={customerLob}
                indexList={indexList}
                suggestionIndex={resultsIndex}
                resultsIndex={resultsIndex}
                queryIndex={queryIndex}
                filterType={filterType}
                searchBarPlaceholderText={searchBarPlaceholderText}
                numResults={numResults}
                numHighlightedResults={numHighlightedResults}
                numberQueries={numberQueries}
                numCharacters={numCharacters}
                errorMessageTitle={errorMessageTitle}
                errorMessageDescription={errorMessageDescription}
                failureTitle={failureTitle}
                failureDescription={failureDescription}
                suggestedText={suggestedText}
                resultsText={resultsText}
                seeAllResultsText={seeAllResultsText}
                searchDelay={searchDelay}
              />
              {isMyWMApp && (
                <GlobalNotifications
                  alerts={alerts}
                  onAlertsChange={this.onAlertsChange}
                  onAlertClose={this.onAlertClose}
                />
              )}
            </div>
          ) : (
            <div
              className="mobile-header"
              style={{
                display:
                  isClientSideLoaded &&
                  ssrWindow.innerWidth < BOOTSTRAP_GRID_LG_MIN
                    ? "block"
                    : "none",
              }}
            >
              {!this.state.isMenuOpen && (
                <GlobalNotifications
                  hideMajorAlert={Boolean(
                    get(customerAddress, `formattedAddress`, ``),
                  )}
                  alerts={alerts}
                  onAlertsChange={this.onAlertsChange}
                  onAlertClose={this.onAlertClose}
                />
              )}
              <MobileHeader
                navigationDetails={navigationDetails}
                closeMegaNav={this.closeMegaNav}
                isSingleAccount={isSingleAccount}
                utilityBarLinks={utilityBarLinks}
                getCartDetails={getCartDetails}
                ecommerceCart={ecommerceCart}
                onMenuClick={this.onMenuClick}
                isMenuOpen={this.state.isMenuOpen}
                languageRoute={languageRoute}
                logo={logo}
                logoLink={logoLink}
                logoAltText={logoAltText}
                customerLob={customerLob}
                copyright={copyright}
                legalLinks={legalLinks}
                siteLanguage={siteLanguage}
                hasFrPage={hasFrPage}
                pageLocales={pageLocales}
                setParentState={this.setParentState}
                pageData={pageData}
                userState={userState}
                userProducts={userProducts}
                setShowMobileSearch={setShowMobileSearch}
                showMobileSearch={showMobileSearch}
                isSupportSearch={isSupportSearch}
                indexList={indexList}
                suggestionIndex={resultsIndex}
                resultsIndex={resultsIndex}
                queryIndex={queryIndex}
                filterType={filterType}
                searchBarPlaceholderText={searchBarPlaceholderText}
                numResults={numResults}
                numHighlightedResults={numHighlightedResults}
                numberQueries={numberQueries}
                numCharacters={numCharacters}
                errorMessageTitle={errorMessageTitle}
                errorMessageDescription={errorMessageDescription}
                failureTitle={failureTitle}
                failureDescription={failureDescription}
                suggestedText={suggestedText}
                resultsText={resultsText}
                seeAllResultsText={seeAllResultsText}
                searchDelay={searchDelay}
                createProfileText={createProfileText}
              />
            </div>
          )}
        </div>
        {megaNavSelection ? this.renderMegaNav(totalAlertHeight) : null}
      </header>
    )
  }
}

Header.propTypes = {
  navigation: PropTypes.string,
  copyright: PropTypes.string.isRequired,
  hasFrPage: PropTypes.bool,
  ecommerceCart: PropTypes.object.isRequired,
  getCartDetails: PropTypes.func.isRequired,
  isSingleAccount: PropTypes.bool.isRequired,
  legalLinks: PropTypes.array.isRequired,
  pageLocales: PropTypes.array,
  siteLanguage: PropTypes.object.isRequired,
  languageRoute: PropTypes.string.isRequired,
  location: PropTypes.object,
  userId: PropTypes.string.isRequired,
  userProducts: PropTypes.object.isRequired,
  notifications: PropTypes.array.isRequired,
  notificationsFetchState: PropTypes.string.isRequired,
  notificationsLanguage: PropTypes.string.isRequired,
  getGlobalAlerts: PropTypes.func.isRequired,
  getIncidentsByUserId: PropTypes.func.isRequired,
  getNotificationsByAddress: PropTypes.func.isRequired,
  getNotificationsByUserId: PropTypes.func.isRequired,
  alertsRead: PropTypes.bool.isRequired,
  customerAddress: PropTypes.object.isRequired,
  userAddress: PropTypes.object.isRequired,
  getCombinedProductsUrl: PropTypes.func.isRequired,
  setCustomerLob: PropTypes.func.isRequired,
  getCombinedProductUrlStatus: PropTypes.string.isRequired,
  userState: PropTypes.number.isRequired,
  combinedProductSupportData: PropTypes.object.isRequired,
  selectedLob: PropTypes.string.isRequired,
  setShowMobileSearch: PropTypes.func.isRequired,
  showMobileSearch: PropTypes.bool.isRequired,
  isSupportSearch: PropTypes.bool.isRequired,
  indexList: PropTypes.array.isRequired,
  queryIndex: PropTypes.string.isRequired,
  filterType: PropTypes.string.isRequired,
  searchBarPlaceholderText: PropTypes.string.isRequired,
  numResults: PropTypes.number.isRequired,
  numHighlightedResults: PropTypes.number.isRequired,
  numberQueries: PropTypes.number.isRequired,
  numCharacters: PropTypes.number.isRequired,
  errorMessageTitle: PropTypes.string.isRequired,
  errorMessageDescription: PropTypes.string.isRequired,
  failureTitle: PropTypes.string.isRequired,
  failureDescription: PropTypes.string.isRequired,
  suggestedText: PropTypes.string.isRequired,
  resultsText: PropTypes.string.isRequired,
  seeAllResultsText: PropTypes.string.isRequired,
  setHeaderStatus: PropTypes.func,
  incidents: PropTypes.array.isRequired,
  isHeaderRendered: PropTypes.bool,
  getNotificationsByCustomerIdGuest: PropTypes.func,
  isAddressSearch: PropTypes.bool.isRequired,
  customerId: PropTypes.string,
  guestCustomerIdToken: PropTypes.string,
  linkedAccounts: PropTypes.array,
  makeMinorAlertVisible: PropTypes.func.isRequired,
  resultsIndex: PropTypes.string.isRequired,
  loginDropdownLabel: PropTypes.string.isRequired,
  loginEmailPlaceholderText: PropTypes.string.isRequired,
  loginPasswordPlaceholderText: PropTypes.string.isRequired,
  loginButtonCta: PropTypes.string.isRequired,
  forgotEmailPwText: PropTypes.string.isRequired,
  continueAsGuestText: PropTypes.string.isRequired,
  createProfileText: PropTypes.string.isRequired,
  myWmDropdownLabel: PropTypes.string.isRequired,
  myWmMenuHeading: PropTypes.string.isRequired,
  logoutCtaText: PropTypes.string.isRequired,
  localsitesCdn: PropTypes.object,
  setMenuOpen: PropTypes.func.isRequired,
  /* AEM props. */
  logo: PropTypes.string.isRequired,
  logoLink: PropTypes.string.isRequired,
  logoAltText: PropTypes.string.isRequired,
  alerts: PropTypes.array.isRequired,
  navigationDetails: PropTypes.array.isRequired,
  utilityBarLinks: PropTypes.array.isRequired,
  customerLob: PropTypes.string.isRequired,
  searchDelay: PropTypes.number,
  ssrModel: PropTypes.object, // this is the model coming from the next js app
  firstName: PropTypes.string.isRequired,
  isLoggedIn: PropTypes.bool.isRequired,
  facilityLocator: PropTypes.object.isRequired,
}

Header.defaultProps = {
  navigation: "",
  hasFrPage: true,
  pageLocales: [],
  searchDelay: DEFAULT_SEARCH_DELAY,
  ssrModel: {},
  location: {},
  isHeaderRendered: false,
  customerId: "",
  guestCustomerIdToken: "",
  linkedAccounts: [],
  localsitesCdn: {},
  setHeaderStatus: noop,
  getNotificationsByCustomerIdGuest: noop,
}

export default withLDConsumer()(withAuthentication(Header))
